import React from "react";

import classNames from "classnames";
import parse from "html-react-parser";
import { Row, Col, Form, Button, Container } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller } from "react-hook-form";
import Select from "react-select";

import { useCaptchaHandlers } from "../hooks/useCaptchaHandlers";
import { useFormHandlers } from "../hooks/useFormHandlers";
import { RegisterProps } from "../types/register";

import "../../../../assets/css/umphub/pages/contact.scss";
import { selectSetting } from "../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { displayFormAlert } from "../../../../lib/utils";

const RegistrationForm = (props: RegisterProps): React.JSX.Element => {
    const { methods, recaptcha } = props;
    const termsAndConditions = useAppSelector(state =>
        selectSetting(state, "umphub_registration_policy_procedures"),
    );
    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isValid },
    } = methods;
    const { onChangeRecaptcha } = useCaptchaHandlers(props);
    const { onSubmit, onError } = useFormHandlers(props);

    return (
        <div data-testid="registerContent">
            <Container className={classNames("pt-3", "pb-5", "register")}>
                <Form
                    onSubmit={handleSubmit(onSubmit, onError)}
                    id="registerForm"
                    data-testid="registerUmpireForm"
                >
                    <Row>
                        <Col>
                            <p>
                                All umpires must register and umpires 18 and
                                older must pass a background check in order to
                                access our site. Please provide the requested
                                info below.
                            </p>
                            <p>
                                We will contact you via email once the
                                background check is complete to let you know if
                                we are able to approve your registration,
                                generally in 48 hours or less. Once approved you
                                can login and begin selecting games.
                            </p>
                            <p>
                                Any questions, please please click on the
                                CONTACT tab at the top of the page.
                            </p>
                        </Col>
                    </Row>
                    <Row className={classNames("mt-3")}>
                        <Col>
                            <h2>Umpire Registration</h2>
                        </Col>
                    </Row>
                    <Row>
                        <Form.Label>Name *</Form.Label>
                        <Form.Group as={Col} controlId="firstName">
                            <Form.Control
                                type="text"
                                {...register("firstName")}
                                aria-invalid={
                                    errors.firstName ? "true" : "false"
                                }
                            />
                            <Form.Label>First</Form.Label>
                            {errors.firstName &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.firstName.message as string,
                                })}
                        </Form.Group>
                        <Form.Group as={Col} controlId="lastName">
                            <Form.Control
                                type="text"
                                {...register("lastName")}
                                aria-invalid={
                                    errors.lastName ? "true" : "false"
                                }
                            />
                            <Form.Label>Last</Form.Label>
                            {errors.lastName &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.lastName.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Label>Address *</Form.Label>
                        <Form.Group as={Col} controlId="address">
                            <Form.Control
                                type="text"
                                {...register("address")}
                                aria-invalid={errors.address ? "true" : "false"}
                            />
                            <Form.Label>Street Address</Form.Label>
                            {errors.address &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.address.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} controlId="address2">
                            <Form.Control
                                type="text"
                                {...register("address2")}
                                aria-invalid={
                                    errors.address2 ? "true" : "false"
                                }
                            />
                            <Form.Label>Address Line 2</Form.Label>
                            {errors.address2 &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.address2.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} controlId="city">
                            <Form.Control
                                type="text"
                                {...register("city")}
                                aria-invalid={errors.city ? "true" : "false"}
                            />
                            <Form.Label>City</Form.Label>
                            {errors.city &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.city.message as string,
                                })}
                        </Form.Group>
                        <Form.Group as={Col} controlId="state">
                            <Form.Control
                                type="text"
                                {...register("state")}
                                aria-invalid={errors.state ? "true" : "false"}
                            />
                            <Form.Label>State</Form.Label>
                            {errors.state &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.state.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} xs={6} controlId="zipCode">
                            <Form.Control
                                type="text"
                                {...register("zipCode")}
                                aria-invalid={errors.zipCode ? "true" : "false"}
                            />
                            <Form.Label>Zip Code</Form.Label>
                            {errors.zipCode &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.zipCode.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Label>Phone *</Form.Label>
                        <Form.Group as={Col} xs={6} controlId="phone">
                            <Form.Control
                                type="text"
                                {...register("phone")}
                                aria-invalid={errors.phone ? "true" : "false"}
                            />
                            <Form.Label>Phone</Form.Label>
                            {errors.phone &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.phone.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Label>Date of Birth *</Form.Label>
                        <Form.Group
                            as={Col}
                            xs={6}
                            md={3}
                            lg={2}
                            controlId="dob"
                        >
                            <Form.Control
                                type="text"
                                {...register("dob")}
                                placeholder="mm/dd/yyyy"
                                aria-invalid={errors.dob ? "true" : "false"}
                            />
                            {errors.dob &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.dob.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="ssn">
                                <Form.Label>SSN *</Form.Label>
                                <Form.Control
                                    {...register("ssn")}
                                    type="text"
                                    maxLength={11}
                                    aria-invalid={errors.ssn ? "true" : "false"}
                                />
                                <Form.Label>
                                    I certify my SSN is correct, I am not
                                    subject to backup withholding and am a US
                                    Person.
                                </Form.Label>
                                {errors.ssn &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.ssn.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Form.Group as={Col} xs={6} controlId="race">
                            <Form.Label>Race *</Form.Label>
                            <Controller
                                name="race"
                                control={control}
                                rules={{
                                    required: "Race is required",
                                }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isSearchable={false}
                                        options={[
                                            "White",
                                            "Black",
                                            "Hispanic",
                                            "Other",
                                        ].map(r => {
                                            return {
                                                value: r,
                                                label: r,
                                            };
                                        })}
                                    />
                                )}
                            />
                            {errors.race &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.race.value?.message as string,
                                })}
                        </Form.Group>
                        <Form.Group as={Col} xs={6} controlId="gender">
                            <Form.Label>Gender *</Form.Label>
                            <Controller
                                name="gender"
                                control={control}
                                rules={{
                                    required: "Gender is required",
                                }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isSearchable={false}
                                        options={["Male", "Female"].map(r => {
                                            return {
                                                value: r,
                                                label: r,
                                            };
                                        })}
                                    />
                                )}
                            />
                            {errors.gender &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.gender.value
                                        ?.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Label>Email *</Form.Label>
                        <Form.Group as={Col} controlId="email">
                            <Form.Control
                                type="email"
                                {...register("email")}
                                aria-invalid={errors.email ? "true" : "false"}
                            />
                            <Form.Label>Enter Email</Form.Label>
                            {errors.email &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.email.message as string,
                                })}
                        </Form.Group>
                        <Form.Group as={Col} controlId="confirmEmail">
                            <Form.Control
                                type="email"
                                {...register("confirmEmail")}
                                aria-invalid={
                                    errors.confirmEmail ? "true" : "false"
                                }
                            />
                            <Form.Label>Confirm Email</Form.Label>
                            {errors.confirmEmail &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.confirmEmail.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Label>Password *</Form.Label>
                        <Form.Group as={Col} controlId="password">
                            <Form.Control
                                type="password"
                                {...register("password")}
                                aria-invalid={
                                    errors.password ? "true" : "false"
                                }
                            />
                            <Form.Label>Enter Password</Form.Label>
                            {errors.password &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.password.message as string,
                                })}
                        </Form.Group>
                        <Form.Group as={Col} controlId="confirmPassword">
                            <Form.Control
                                type="password"
                                {...register("confirmPassword")}
                                aria-invalid={
                                    errors.confirmPassword ? "true" : "false"
                                }
                            />
                            <Form.Label>Confirm Password</Form.Label>
                            {errors.confirmPassword &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.confirmPassword
                                        .message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col xs={3}>
                            <Form.Label>CAPTCHA</Form.Label>
                            <ReCAPTCHA
                                ref={recaptcha}
                                sitekey={
                                    process.env.RECAPTCHA_SITE_KEY as string
                                }
                                onChange={onChangeRecaptcha}
                            />
                            {errors.recaptcha &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.recaptcha.message as string,
                                })}
                        </Col>
                    </Row>
                    <Row>
                        <Form.Group
                            as={Col}
                            controlId="tac"
                            className={classNames("tac")}
                        >
                            <div>{parse(termsAndConditions?.setting)}</div>
                            <Form.Check
                                inline
                                label="I agree to the terms and conditions above"
                                type="checkbox"
                                className="checkbox-xl"
                                {...register("tac")}
                                aria-invalid={errors.tac ? "true" : "false"}
                            />
                            {errors.tac &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.tac.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col>
                            <Button variant="primary" type="submit">
                                Submit
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Container>
        </div>
    );
};

export default RegistrationForm;
